// frontend/src/pages/Settings/SubscriptionManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Card, CardContent,
  Chip, Divider, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, TextField,
  LinearProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Pagination
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import api from '../../services/api';

const TIER_LABELS = { free: 'Free', pro: 'Pro', family: 'Family', school: 'Schools' };
const STATUS_LABELS = {
  active: { label: 'Active', color: 'success', icon: <CheckCircleIcon /> },
  trialing: { label: 'Trial', color: 'info', icon: <CheckCircleIcon /> },
  past_due: { label: 'Past Due', color: 'warning', icon: <WarningAmberIcon /> },
  cancelled: { label: 'Cancelled', color: 'error', icon: <CancelIcon /> },
  expired: { label: 'Expired', color: 'default', icon: <CancelIcon /> }
};

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState({ events: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const fetchData = useCallback(async (invoicePage = 1) => {
    try {
      const [statusRes, invoicesRes] = await Promise.all([
        api.get('/api/subscription/status'),
        api.get(`/api/subscription/invoices?page=${invoicePage}&limit=10`)
      ]);

      if (statusRes.data.success) setSub(statusRes.data.data);
      if (invoicesRes.data.success) setInvoices(invoicesRes.data.data);
    } catch (err) {
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/subscription/cancel', { reason: cancelReason });
      if (res.data.success) {
        setSuccess(res.data.data.message);
        setCancelDialog(false);
        setCancelReason('');
        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/subscription/reactivate');
      if (res.data.success) {
        setSuccess(res.data.data.message);
        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reactivate');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const statusInfo = STATUS_LABELS[sub?.status] || STATUS_LABELS.active;
  const isPaid = sub && sub.tier !== 'free';
  const isCancelled = sub?.status === 'cancelled';
  const isTrialing = sub?.isTrialing;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Subscription
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ============================================================ */}
      {/* Current Plan Card */}
      {/* ============================================================ */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  MathZA {TIER_LABELS[sub?.tier] || 'Free'}
                </Typography>
                <Chip
                  icon={statusInfo.icon}
                  label={statusInfo.label}
                  color={statusInfo.color}
                  size="small"
                  variant="outlined"
                />
                {sub?.adminOverride && (
                  <Chip label="Admin Granted" size="small" color="secondary" variant="outlined" />
                )}
              </Box>

              {isTrialing && (
                <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2">
                    Your free trial ends in <strong>{sub.trialDaysLeft} day{sub.trialDaysLeft !== 1 ? 's' : ''}</strong>.
                    {sub.trialDaysLeft <= 3 ? ' Subscribe now to keep your Pro features!' : ''}
                  </Typography>
                </Alert>
              )}

              {isPaid && sub.billingCycle && (
                <Typography variant="body2" color="text.secondary">
                  Billed {sub.billingCycle} · Next billing date: {sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-ZA') : '—'}
                </Typography>
              )}

              {isCancelled && sub.cancelAtPeriodEnd && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Your subscription is cancelled but you have access until <strong>{new Date(sub.currentPeriodEnd).toLocaleDateString('en-ZA')}</strong>.
                  </Typography>
                </Alert>
              )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {sub?.tier === 'free' && (
                <Button
                  variant="contained"
                  startIcon={<UpgradeIcon />}
                  onClick={() => navigate('/pricing')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Upgrade
                </Button>
              )}

              {isTrialing && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/pricing')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Subscribe Now
                </Button>
              )}

              {isCancelled && sub.cancelAtPeriodEnd && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleReactivate}
                  disabled={actionLoading}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Reactivate'}
                </Button>
              )}

              {isPaid && !isCancelled && !sub.adminOverride && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setCancelDialog(true)}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel Plan
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* Usage / Limits Card */}
      {/* ============================================================ */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Usage
          </Typography>

          {/* Bookmarks */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Saved Items</Typography>
              <Typography variant="body2" color="text.secondary">
                {sub?.limits?.bookmarks?.unlimited
                  ? `${sub.limits.bookmarks.current} (unlimited)`
                  : `${sub?.limits?.bookmarks?.current || 0} / ${sub?.limits?.bookmarks?.limit || 20}`
                }
              </Typography>
            </Box>
            {!sub?.limits?.bookmarks?.unlimited && (
              <LinearProgress
                variant="determinate"
                value={Math.min(((sub?.limits?.bookmarks?.current || 0) / (sub?.limits?.bookmarks?.limit || 20)) * 100, 100)}
                sx={{ borderRadius: 1, height: 6 }}
              />
            )}
          </Box>

          {/* Hints */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Hints per Question</Typography>
              <Typography variant="body2" color="text.secondary">
                {sub?.limits?.hints?.unlimited ? 'Unlimited' : `${sub?.limits?.hints?.perQuestion || 2} per question`}
              </Typography>
            </Box>
          </Box>

          {sub?.tier === 'free' && (
            <Button
              size="small"
              onClick={() => navigate('/pricing')}
              sx={{ mt: 1, textTransform: 'none', color: 'primary.main' }}
            >
              Upgrade for unlimited hints & bookmarks →
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* Billing History */}
      {/* ============================================================ */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ReceiptIcon color="action" />
            <Typography variant="h6" fontWeight={600}>
              Billing History
            </Typography>
          </Box>

          {invoices.events.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No billing events yet
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.events.map((ev) => (
                      <TableRow key={ev._id}>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(ev.createdAt).toLocaleDateString('en-ZA')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{ev.description || ev.eventType}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={ev.amount ? 600 : 400}>
                            {ev.amount ? `R${(ev.amount / 100).toFixed(2)}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ev.status}
                            size="small"
                            color={ev.status === 'completed' ? 'success' : ev.status === 'failed' ? 'error' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {invoices.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={invoices.totalPages}
                    page={invoices.page}
                    onChange={(e, page) => fetchData(page)}
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* Cancel Dialog */}
      {/* ============================================================ */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Your Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You'll keep access to {TIER_LABELS[sub?.tier]} features until {sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-ZA') : 'the end of your billing period'}. After that, you'll be downgraded to the Free plan.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2, fontWeight: 600 }}>
            You can reactivate anytime before your billing period ends.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Why are you leaving? (optional)"
            placeholder="Your feedback helps us improve MathZA"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelDialog(false)} sx={{ textTransform: 'none' }}>
            Keep My Plan
          </Button>
          <Button
            onClick={handleCancel}
            color="error"
            variant="contained"
            disabled={actionLoading}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubscriptionManagement;
